import React, { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import Modal from "@/components/Modal";
import ConfirmationModal from "@/components/ConfirmationModal";
import SizingToolFormManual from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/components/SizingToolFormManual";
import SizingToolFormProject from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/components/SizingToolFormProject";
import { SizingToolResultModal } from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/components/SizingToolResultModal";
import {
  SizingFormData,
  SizingResult,
  SizingModalType,
  SizingToolState,
} from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/types";
import { ProjectRecord } from "@/types";
import { analyzeSizingToolReq, getProjectReq } from "@/api/projectsApi";
import { useGlobalSizingToolStore } from "@/store/globalSizingToolStore";
import { useProjectsStore } from "@/store/projectsStore";
import { calculateTotalUsableCapacity } from "@/utils/calculateTotalNominalPower";
import { useFileValidation } from "@/hooks/useFileValidation";
import { createEmptySizingFormData } from "@/pages/ProjectDetailsPage/components/ProjectInfoCard/utils/sizingToolUtils";

interface GlobalSizingToolModalProps {
  open: boolean;
  onClose: () => void;
  projectData?: ProjectRecord | null;
}

export const GlobalSizingToolModal: React.FC<GlobalSizingToolModalProps> = ({
  open,
  onClose,
  projectData,
}) => {
  const { forceManualMode, isDemo } = useGlobalSizingToolStore();
  const setProjectDetails = useProjectsStore(
    (state) => state.setProjectsDetails
  );
  const params = useParams();
  const { validateFile } = useFileValidation();

  const [currentProjectData, setCurrentProjectData] =
    useState<ProjectRecord | null>(projectData || null);
  const [showTerminationConfirmation, setShowTerminationConfirmation] =
    useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  React.useEffect(() => {
    if (projectData) {
      setCurrentProjectData(projectData);
    }
  }, [projectData]);
  const [state, setState] = useState<SizingToolState>({
    openSizingTool: open,
    sizingForm: createEmptySizingFormData(),
    analyzing: false,
    sizingResult: null,
    sizingModalType: "form",
    sizingError: null,
    showUpload: false,
    uploading: false,
    uploadSuccess: false,
    file: null,
    sizingPassed: 0,
    sizingSubmitted: 0,
    sizingStatus: "not_run",
  });

  const methods = useForm<SizingFormData>({
    defaultValues: createEmptySizingFormData(),
  });

  const updateState = useCallback((updates: Partial<SizingToolState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const updateSizingForm = useCallback((updates: Partial<SizingFormData>) => {
    setState((prev) => ({
      ...prev,
      sizingForm: { ...prev.sizingForm, ...updates },
    }));
  }, []);

  const handleAnalyze = useCallback(
    async (formValues?: SizingFormData) => {
      if (!formValues || formValues?.hasExistingBattery) return;

      const controller = new AbortController();
      setAbortController(controller);

      updateState({
        analyzing: true,
        sizingError: null,
        sizingForm: formValues,
        sizingSubmitted: 1,
      });

      try {
        const requestBody = {
          accountNumber: formValues.accountNumber,
          existingSolarSystemSize: Number(formValues.existingSolarKwdc) || 0,
          newSolarSystemSize: Number(formValues.newSolarKwdc) || 0,
          totalBatteryUsableCapacity:
            Number(formValues.totalBatteryUsableCapacity) || 0,
          numberOfBatteries: Number(formValues.batteryQuantity) || 0,
          zipCode: formValues.zipCode,
          solar_installation_duration_less_than_12_months:
            formValues.existingSolarLessThan12Months === "yes",
          existing_battery_installed: formValues.hasExistingBattery || false,
          ...(isDemo && { isDemo: true }),
        };

        const response = await analyzeSizingToolReq(
          requestBody,
          controller.signal
        );
        const result = response.data;

        if (result.calculationFailed) {
          updateState({
            analyzing: false,
            sizingResult: result,
            sizingModalType: "calculationFailure",
            showUpload: true,
          });
        } else if (result.isSufficient !== undefined) {
          updateState({
            analyzing: false,
            sizingResult: result,
            sizingModalType: result.isSufficient ? "success" : "insufficient",
            sizingPassed: result.sizing_passed || 0,
            sizingSubmitted: result.sizing_submitted || 1,
            sizingStatus: result.sizing_status || "not_run",
          });

          if (setProjectDetails && currentProjectData && !isDemo) {
            setProjectDetails({
              ...currentProjectData,
              sizing_passed: result.isSufficient ? 1 : 0,
              sizing_submitted:
                result.sizing_submitted || (result.isSufficient ? 1 : 0),
              sizing_status: result.sizing_status,
            });
          }
        } else if (result.message || result.resultMessage) {
          updateState({
            analyzing: false,
            sizingResult: result,
            sizingModalType: "genericError",
          });
        } else {
          updateState({
            analyzing: false,
            sizingModalType: "genericError",
            sizingError: "Unable to analyze sizing data",
          });
        }
      } catch (error: any) {
        if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
          updateState({ analyzing: false });
          return;
        }

        // Check if the error response contains calculationFailed
        if (error?.response?.data?.calculationFailed) {
          updateState({
            analyzing: false,
            sizingResult: error.response.data,
            sizingModalType: "calculationFailure",
            showUpload: true,
          });
        } else if (error?.response?.data?.error) {
          const errorMessage = error.response.data.error;
          // Check for specific error patterns that indicate calculation failure
          if (
            errorMessage.includes("insufficient data") ||
            errorMessage.includes("less than 6 months") ||
            errorMessage.includes("calculation failed")
          ) {
            updateState({
              analyzing: false,
              sizingResult: error.response.data,
              sizingModalType: "calculationFailure",
              showUpload: true,
            });
          } else {
            updateState({
              analyzing: false,
              sizingModalType: "genericError",
              sizingError: errorMessage,
            });
          }
        } else {
          updateState({
            analyzing: false,
            sizingModalType: "genericError",
            sizingError:
              error?.response?.data?.message ||
              "Analysis failed. Please try again.",
          });
        }
      } finally {
        setAbortController(null);
      }
    },
    [updateState, projectData, currentProjectData, params]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const { isValid, error } = validateFile(file);

        if (isValid) {
          updateState({
            file,
            sizingError: null,
          });
        } else {
          updateState({
            sizingError: error,
            file: null,
          });
        }
      } else {
        updateState({ file: null });
      }
    },
    [updateState, validateFile]
  );

  const handleUpload = useCallback(async () => {
    if (!state.file) return;

    updateState({ uploading: true, sizingError: null });

    try {
      // For global modal, we don't have a project ID to upload to
      // This would need to be implemented based on your requirements
      // For now, we'll just show a success message
      updateState({
        uploadSuccess: true,
        showUpload: false,
        openSizingTool: false,
      });
    } catch (e) {
      updateState({
        sizingError: "File upload failed. Please try again.",
      });
    }

    updateState({ uploading: false });
  }, [state.file, updateState]);

  const handleClose = () => {
    const emptyForm = createEmptySizingFormData();
    methods.reset(emptyForm);

    setState({
      openSizingTool: false,
      sizingForm: emptyForm,
      analyzing: false,
      sizingResult: null,
      sizingModalType: "form",
      sizingError: null,
      showUpload: false,
      uploading: false,
      uploadSuccess: false,
      file: null,
      sizingPassed: 0,
      sizingSubmitted: 0,
      sizingStatus: "not_run",
    });

    onClose();
  };

  const renderModalContent = () => {
    if (state.sizingModalType !== "form") {
      const currentFormValues = methods.getValues();
      const mergedFormData: SizingFormData = {
        ...state.sizingForm,
        ...currentFormValues,
      };

      return (
        <SizingToolResultModal
          type={state.sizingModalType}
          sizingResult={state.sizingResult}
          sizingError={state.sizingError}
          accountHash={methods.getValues("accountNumber") || "N/A"}
          customerName={methods.getValues("customerName") || "N/A"}
          projectId={projectData?.id}
          projectDetails={projectData}
          sizingFormData={mergedFormData}
          onBack={() => updateState({ sizingModalType: "form" })}
          onAnalyze={handleAnalyze}
          onUpload={() => {}}
          analyzing={state.analyzing}
          file={state.file}
          setFile={(newFile) => updateState({ file: newFile })}
          handleFileChange={handleFileChange}
          handleUpload={handleUpload}
          uploading={state.uploading}
          uploadSuccess={state.uploadSuccess}
          hideUploadAndNextSteps={!projectData}
          onClose={handleCloseRequest}
        />
      );
    }

    return (
      <SizingToolFormManual
        sizingForm={state.sizingForm}
        setSizingForm={updateSizingForm}
        handleAnalyze={handleAnalyze}
        analyzing={state.analyzing}
        sizingSubmitted={state.sizingSubmitted}
        setOpenSizingTool={(open: boolean) =>
          updateState({ openSizingTool: open })
        }
        useForm={() => methods}
        handleSizingInput={(e: React.ChangeEvent<HTMLInputElement>) => {
          updateSizingForm({ [e.target.name]: e.target.value });
        }}
        handleCloseRequest={handleCloseRequest}
        showNewFields={true}
        showSolarSystemCheckboxes={isDemo && !projectData}
        projectData={projectData}
      />
    );
  };

  React.useEffect(() => {
    if (projectData) {
      const solarSystemTypes: string[] = [];
      if (projectData.solarKwdc && Number(projectData.solarKwdc) > 0) {
        solarSystemTypes.push("existing");
      }
      if (projectData.newSolarKwdc && Number(projectData.newSolarKwdc) > 0) {
        solarSystemTypes.push("new");
      }

      const formData = {
        accountNumber: projectData.accountHash || "",
        zipCode: projectData.zipCode || "",
        customerName: projectData.customerName || "",
        solarSystemType: solarSystemTypes,
        existingSolarKwdc: String(projectData.solarKwdc || ""),
        existingSolarKwac: String(projectData.solarKwac || ""),
        existingSolarAnnualProduction: String(
          projectData.solarAnnualProduction || ""
        ),
        existingSolarLessThan12Months: "",
        newSolarKwdc: String(projectData.newSolarKwdc || ""),
        newSolarKwac: String(projectData.newSolarKwac || ""),
        newSolarAnnualProduction: String(
          projectData.newSolarAnnualProduction || ""
        ),
        hasExistingBattery: false,
        batteryManufacturer: projectData.batteryOem || "",
        batteryManufacturerId: projectData.oemId
          ? String(projectData.oemId)
          : "",
        batteryProductName: projectData.batteryModel || "",
        batteryQuantity: String(projectData.numberOfBatteries || ""),
        nominalPower: String(
          projectData.nominalPowerkw || projectData.ptoNameplateKw || ""
        ),
        usableCapacity: String(projectData.usableCapacity || ""),
        totalBatteryUsableCapacity: calculateTotalUsableCapacity(
          String(projectData.usableCapacity || ""),
          String(projectData.numberOfBatteries || "")
        ),
      };

      setState((prev) => ({
        ...prev,
        sizingForm: formData,
      }));

      methods.reset(formData);
    } else {
      setState((prev) => ({
        ...prev,
        sizingForm: createEmptySizingFormData(),
      }));
    }
  }, [projectData]);

  React.useEffect(() => {
    if (open) {
      if (projectData) {
        const solarSystemTypes: string[] = [];
        if (projectData.solarKwdc && Number(projectData.solarKwdc) > 0) {
          solarSystemTypes.push("existing");
        }
        if (projectData.newSolarKwdc && Number(projectData.newSolarKwdc) > 0) {
          solarSystemTypes.push("new");
        }

        const formData = {
          accountNumber: projectData.accountHash || "",
          zipCode: projectData.zipCode || "",
          customerName: projectData.customerName || "",
          solarSystemType: solarSystemTypes,
          existingSolarKwdc: String(projectData.solarKwdc || ""),
          existingSolarKwac: String(projectData.solarKwac || ""),
          existingSolarAnnualProduction: String(
            projectData.solarAnnualProduction || ""
          ),
          existingSolarLessThan12Months: "",
          newSolarKwdc: String(projectData.newSolarKwdc || ""),
          newSolarKwac: String(projectData.newSolarKwac || ""),
          newSolarAnnualProduction: String(
            projectData.newSolarAnnualProduction || ""
          ),
          hasExistingBattery: false,
          batteryManufacturer: projectData.batteryOem || "",
          batteryManufacturerId: projectData.oemId
            ? String(projectData.oemId)
            : "",
          batteryProductName: projectData.batteryModel || "",
          batteryQuantity: String(projectData.numberOfBatteries || ""),
          nominalPower: String(
            projectData.nominalPowerkw || projectData.ptoNameplateKw || ""
          ),
          usableCapacity: String(projectData.usableCapacity || ""),
          totalBatteryUsableCapacity: calculateTotalUsableCapacity(
            String(projectData.usableCapacity || ""),
            String(projectData.numberOfBatteries || "")
          ),
        };
        setState((prev) => ({
          ...prev,
          sizingForm: formData,
        }));
      } else {
        const emptyForm = createEmptySizingFormData();
        methods.reset(emptyForm);
        setState((prev) => ({
          ...prev,
          sizingForm: emptyForm,
          sizingModalType: "form",
          sizingResult: null,
          sizingError: null,
        }));
      }
    }
  }, [open, projectData]);

  const handleCloseRequest = () => {
    if (open && state.analyzing) {
      setShowTerminationConfirmation(true);
    } else {
      handleClose();
    }
  };

  const handleTerminationConfirm = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setShowTerminationConfirmation(false);
    handleClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseRequest}
        title="System Sizing Tool"
        disableBackdropClick={false}
        boxSx={{
          maxWidth: 750,
          minWidth: 569,
          width: "750px",
          maxHeight: "90vh",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.10)",
          padding: "32px",
          overflowY: "auto",
          "& > *:first-of-type": {
            marginBottom: "24px",
          },
          "& > *:not(:first-of-type)": {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        {renderModalContent()}
      </Modal>

      <ConfirmationModal
        open={showTerminationConfirmation}
        onClose={() => setShowTerminationConfirmation(false)}
        onConfirm={handleTerminationConfirm}
        title="Terminate System Sizing Process"
        message="Are you sure you want to terminate the system sizing process?"
        confirmText="Yes"
        cancelText="No"
        confirmButtonColor="#DC3545"
      />
    </>
  );
};
